// src/components/shared/HeroSlider.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';

// ─── Default slides shown before any admin slides are saved ───────────────────
const DEFAULT_SLIDES = [
  {
    id: 'default-1',
    image: '',           // empty = gradient placeholder
    eyebrow: 'Just Dropped',
    title: 'Premium CBD Flower Collection',
    buttonText: 'Shop Now',
    link: '/#cbd-flower',
    duration: 5,
  },
  {
    id: 'default-2',
    image: '',
    eyebrow: 'Artisan Collectibles',
    title: 'Hemp Resin & Extract Specimens',
    buttonText: 'Browse Collection',
    link: '/#hemp-resin',
    duration: 5,
  },
  {
    id: 'default-3',
    image: '',
    eyebrow: 'Best Value',
    title: 'Collector Bundles — Save More',
    buttonText: 'View Bundles',
    link: '/#bundles',
    duration: 5,
  },
];

// Gradient backgrounds for slides without images
const GRADIENT_BG = [
  'linear-gradient(135deg, #0a2e18 0%, #0a0a0a 60%)',
  'linear-gradient(135deg, #1a0a2e 0%, #0a0a0a 60%)',
  'linear-gradient(135deg, #2e1a0a 0%, #0a0a0a 60%)',
];

export default function HeroSlider({ slides: propSlides }) {
  const [slides, setSlides] = useState(propSlides || DEFAULT_SLIDES);
  const [current, setCurrent] = useState(0);
  const [progress, setProgress] = useState(0);
  const timerRef = useRef(null);
  const progressRef = useRef(null);
  const progressStart = useRef(null);

  // Load slides from Supabase if no prop slides given
  useEffect(() => {
    if (propSlides) return;
    supabase
      .from('hero_slides')
      .select('*')
      .eq('active', true)
      .order('sort_order')
      .then(({ data }) => {
        if (data?.length) setSlides(data);
      });
  }, [propSlides]);

  const goTo = useCallback((idx) => {
    setCurrent(idx);
    setProgress(0);
    progressStart.current = Date.now();
  }, []);

  const next = useCallback(() => {
    setSlides(prev => {
      const nextIdx = (current + 1) % prev.length;
      goTo(nextIdx);
      return prev;
    });
  }, [current, goTo]);

  // Progress bar + auto-advance
  useEffect(() => {
    if (!slides.length) return;
    const durationMs = (slides[current]?.duration || 5) * 1000;
    progressStart.current = Date.now();

    const tick = () => {
      const elapsed = Date.now() - progressStart.current;
      const pct = Math.min((elapsed / durationMs) * 100, 100);
      setProgress(pct);
      if (elapsed >= durationMs) {
        const nextIdx = (current + 1) % slides.length;
        setCurrent(nextIdx);
        setProgress(0);
        progressStart.current = Date.now();
      }
      progressRef.current = requestAnimationFrame(tick);
    };
    progressRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(progressRef.current);
  }, [current, slides]);

  if (!slides.length) return null;

  return (
    <div className="hero-slider" role="region" aria-label="Featured products">
      {slides.map((slide, i) => (
        <div
          key={slide.id || i}
          className={`hero-slide ${i === current ? 'active' : ''}`}
          style={!slide.image ? { background: GRADIENT_BG[i % GRADIENT_BG.length] } : undefined}
        >
          {slide.image && (
            <img src={slide.image} alt={slide.title || ''} loading={i === 0 ? 'eager' : 'lazy'} />
          )}
          <div className="hero-content">
            {slide.eyebrow && (
              <span className="hero-eyebrow">{slide.eyebrow}</span>
            )}
            {slide.title && (
              <div className="hero-title">{slide.title}</div>
            )}
          </div>
          {/* Button is a sibling of hero-content so it can be independently positioned on mobile */}
          {slide.link && (
            <Link to={slide.link} className="hero-btn">
              {slide.buttonText || 'Shop Now'} →
            </Link>
          )}
        </div>
      ))}

      {/* Dot indicators */}
      {slides.length > 1 && (
        <div className="hero-dots">
          {slides.map((_, i) => (
            <button
              key={i}
              className={`hero-dot ${i === current ? 'active' : ''}`}
              onClick={() => goTo(i)}
              aria-label={`Go to slide ${i + 1}`}
            />
          ))}
        </div>
      )}

      {/* Progress bar */}
      <div
        className="hero-progress"
        style={{ width: `${progress}%`, transitionDuration: '100ms' }}
      />
    </div>
  );
}